import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { AddDSPData, DSPData } from "../types/types";
import { formatDSPName } from "../../utils/formatDSP";
import TermsOfServiceModal from "./TermsOfServiceModal";
import ArtistTypeahead from "../roster/ArtistTypeahead";
import { artistsApi } from "../../repositories/artists";
import { Artist } from "../roster/types";
import InfoTooltip from "../common/InfoTooltip";
import ArtistTagInput from "../common/ArtistTagInput";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (dspData: AddDSPData) => Promise<void>;
  dspData: DSPData | null;
  selectedDSP?: string | null;
};

const AddDSPModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  dspData,
  selectedDSP,
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [artists,setArtists] = useState<Artist[]>([]);
  useEffect(() => {
    if (isOpen) {
      if (dspData) {
        formik.setValues({
          userName: dspData.name,
          email: dspData.email,
          password: "",
          isChecked: false,
        });
      } else {
        // Adding mode: Reset form
        formik.resetForm();
      }
    }
  }, [isOpen, dspData]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
   useEffect(() => {
      const loadArtists = async () => {
        try {
          const response = await artistsApi.getAll({ limit: 100 });
          const artistsData = response.data.data.artists || [];
          setArtists(artistsData);
        } catch (error) {
          console.error('Error loading artists:', error);
        }
      };
      loadArtists();
    }, []);

  const formik = useFormik({
    initialValues: {
      userName: dspData ? dspData.name : "",
      email: dspData ? dspData.email : "",
      password: "",
      isChecked: false,
    },
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      if (!values.userName) errors.userName = "Name is required";
      if (!values.email) errors.email = "Email is required";
      if (!values.password && !dspData)
        errors.password = "Password is required";
      if (!values.isChecked)
        errors.isChecked = "You must agree to the terms and conditions";

      return errors;
    },
    onSubmit: async (values) => {
      if (!selectedDSP) {
        alert("No DSP selected.");
        return;
      }
      setIsConnecting(true);
      try {
        const dspDataToSubmit: AddDSPData = {
          name: values.userName,
          email: values.email,
          password: values.password || "",
          type: selectedDSP,
        };

        await onSubmit(dspDataToSubmit);
      } catch (error: unknown) {
        console.error("Error in onSubmit:", error);
      } finally {
        setIsConnecting(false); // Stop loading
      }
    },
  });

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50"
        onClick={handleClose}
      >
        <div
          className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <form onSubmit={formik.handleSubmit}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {dspData
                  ? "Edit DSP Connection"
                  : `Connect to ${formatDSPName(selectedDSP || "")}`}
              </h2>
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <p className="mb-4 text-sm text-gray-600">
                Connect your {formatDSPName(selectedDSP || "")} account to
                import your streaming data and improve pitch targeting.
              </p>
            </div>

            <div className="space-y-4">
          

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  className="w-full px-3 py-2 text-sm text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-1 text-sm text-red-500">
                    {formik.errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative w-full">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder={
                      dspData ? "Enter new password" : "Enter your password"
                    }
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    className="w-full px-3 py-2 text-sm text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span
                    className="absolute text-gray-500 transform -translate-y-1/2 cursor-pointer right-3 top-1/2"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </span>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-1 text-sm text-red-500">
                    {formik.errors.password}
                  </p>
                )}
              </div>

              <div>
                 <label className="block mb-1 text-sm font-medium text-gray-700">
                  Artist Name
                  <InfoTooltip content="List the artists linked to this DSP account. Separate names with commas, or indicate that all artists on your roster are on this account." />
                </label>
                
                 <ArtistTagInput
                  value={formik.values.userName}
                  onChange={value => formik.setFieldValue('userName', value)}
                  placeholder="Enter Artist Name"
                />
                {formik.touched.userName && formik.errors.userName && (
                  <p className="mt-1 text-sm text-red-500">
                    {formik.errors.userName}
                  </p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="terms"
                  name="isChecked"
                  checked={formik.values.isChecked}
                  onChange={formik.handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  required
                />
                <label htmlFor="terms" className="ml-2 text-sm text-gray-700">
                  I agree to the{" "}
                  <span
                    className="text-blue-500 underline cursor-pointer hover:text-blue-700"
                    onClick={() => setShowTermsModal(true)}
                  >
                    Terms and Conditions
                  </span>
                </label>
              </div>
              {formik.touched.isChecked && formik.errors.isChecked && (
                <p className="text-sm text-red-500">
                  {formik.errors.isChecked}
                </p>
              )}
            </div>

            <div className="flex justify-end mt-6 space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm text-gray-700 transition border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isConnecting}
                className="px-4 py-2 text-sm text-white transition bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isConnecting ? (
                  <>
                    <span className="inline-block w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></span>
                    {dspData ? "Updating..." : "Connecting..."}
                  </>
                ) : dspData ? (
                  "Update"
                ) : (
                  "Connect"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Terms Modal */}
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onAccept={() => setShowTermsModal(false)}
        onDecline={() => setShowTermsModal(false)}
        viewOnly={true}
      />
    </>
  );
};

export default AddDSPModal;
